<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800"
    overlay-opacity="0.9"
    :retain-focus="false"
    @keydown.esc="false"
    @click:outside="false"
  >
    <v-card class="tac-modal-card">
      <v-card-title class="tac-modal-header">
        <div class="text-h6 font-weight-bold">WEBSITE UPDATES</div>
      </v-card-title>
      <v-divider></v-divider>
      <!-- TAC Content View -->
      <div v-if="!showSuccess">
        <!-- Scrollable Content Container -->
        <v-card-text class="tac-content-container">
          <div 
            ref="contentScroll"
            class="tac-content-scroll"
            @scroll="handleScroll"
            v-html="tacContent"
          ></div>
        </v-card-text>
        <v-divider></v-divider>
        <!-- Checkbox Container -->
        <v-card-text class="tac-checkbox-container">
          <v-checkbox
            v-model="acceptCheckbox"
            color="primary"
            hide-details
            :disabled="!hasScrolledToBottom"
          >
            <template v-slot:label>
              <span class="tac-checkbox-label" :class="{ 'disabled-text': !hasScrolledToBottom }">
                I understand and accept the updated terms and conditions
              </span>
            </template>
          </v-checkbox>
          <div v-if="!hasScrolledToBottom" class="scroll-hint">
            Please scroll to the bottom to read all terms and conditions
          </div>
        </v-card-text>
      </div>
      
      <!-- Success View -->
      <v-card-text v-if="showSuccess" class="tac-modal-content pa-10">
        <v-row>
          <v-col cols="12" class="d-flex justify-center">
            <v-img max-height="75" max-width="75" :src="successImage"></v-img>
          </v-col>
          <v-col cols="12" class="d-flex justify-center">
            <v-card-title
              style="word-break: break-word; width: 80%"
              class="d-flex justify-center tw-font-bold message-header"
            >
              YOU HAVE ACCEPTED THE UPDATED TERMS AND CONDITIONS
            </v-card-title>
          </v-col>
        </v-row>
      </v-card-text>
      
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          v-if="!showSuccess"
          color="error"
          rounded
          class="px-8"
          @click="handleDecline"
          :disabled="isProcessing"
        >
          DECLINE
        </v-btn>
        <v-btn
          v-if="!showSuccess"
          color="success"
          rounded
          class="px-8 ml-3"
          @click="handleAccept"
          :disabled="!acceptCheckbox || isProcessing"
          :loading="isProcessing"
        >
          ACCEPT
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
import Success from "@/assets/images/alert/success.svg";
export default {
  name: "TermsAndConditionsModal",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    tacData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      acceptCheckbox: false,
      isProcessing: false,
      showSuccess: false,
      successImage: Success,
      hasScrolledToBottom: false,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.show;
      },
      set() {
        // Prevent closing by clicking outside or ESC
      },
    },
    tacContent() {
      return this.tacData && this.tacData.content
        ? this.tacData.content.replace(/\n/g, "<br>")
        : "";
    },
  },
  methods: {
    ...mapActions("user", ["updateAcceptTos", "logout"]),
    handleScroll() {
      const scrollContainer = this.$refs.contentScroll;
      if (scrollContainer) {
        const scrollTop = scrollContainer.scrollTop;
        const scrollHeight = scrollContainer.scrollHeight;
        const clientHeight = scrollContainer.clientHeight;
        
        // If content doesn't require scrolling (fits in viewport), enable checkbox immediately
        if (scrollHeight <= clientHeight + 1) { // +1 for rounding differences
          this.hasScrolledToBottom = true;
        } else {
          // Content requires scrolling - only enable if scrolled to bottom
          // Check if scrolled to bottom (with 5px threshold for rounding)
          this.hasScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 5;
        }
      }
    },
    async handleAccept() {
      if (!this.acceptCheckbox) {
        return;
      }
      this.isProcessing = true;
      try {
        const response = await this.updateAcceptTos();
        if (response && response.data && response.data.success) {
          // Show success message first
          this.isProcessing = false;
          this.showSuccess = true;
          
          // Wait 2 seconds to show success message, then refresh the page
          // Don't emit "accepted" event until right before refresh
          // This prevents the parent from closing the modal before success is shown
          setTimeout(() => {
            // The page reload will close the modal naturally, so we don't need to emit
            // But emit it anyway for cleanup if needed
            this.$emit("accepted");
            window.location.reload();
          }, 2000);
        } else {
          this.$toast.error("Failed to accept Terms and Conditions", {
            position: "top",
          });
          this.isProcessing = false;
        }
      } catch (error) {
        console.error("Error accepting TAC:", error);
        this.$toast.error("An error occurred. Please try again.", {
          position: "top",
        });
        this.isProcessing = false;
      }
    },
    async handleDecline() {
      this.isProcessing = true;
      try {
        this.$cookies.remove("_token");
        await this.logout();
        this.$toast.info("You have been logged out", {
          position: "top",
        });
        // Redirect will happen via logout action
      } catch (error) {
        console.error("Error during logout:", error);
        // Force redirect even if logout fails
        window.location.href = "/home?bt-path=%2Flive";
      } finally {
        this.isProcessing = false;
      }
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.acceptCheckbox = false;
        this.showSuccess = false;
        this.hasScrolledToBottom = false;
        // Reset scroll position when modal opens
        this.$nextTick(() => {
          if (this.$refs.contentScroll) {
            // Ensure scroll is at top
            this.$refs.contentScroll.scrollTop = 0;
            // Wait a frame to ensure scroll position is set, then check
            setTimeout(() => {
              this.handleScroll();
            }, 50);
          } else {
            // If ref doesn't exist yet, wait a bit longer
            setTimeout(() => {
              if (this.$refs.contentScroll) {
                this.$refs.contentScroll.scrollTop = 0;
                this.handleScroll();
              }
            }, 100);
          }
        });
      }
    },
    tacContent() {
      // When content changes, re-check if scrolling is needed
      this.$nextTick(() => {
        this.handleScroll();
      });
    },
  },
};
</script>

<style scoped>
.tac-modal-card {
  background-color: #0d111b !important;
  border-radius: 15px !important;
}

.tac-modal-header {
  background: linear-gradient(
    90deg,
    rgba(69, 137, 210, 0.5) 0%,
    rgba(69, 137, 210, 0) 100%
  );
  color: white !important;
  padding: 20px 24px !important;
}

.tac-content-container {
  background-color: #0d111b !important;
  color: white !important;
  padding: 24px !important;
  max-height: 400px;
  overflow: hidden;
}

.tac-content-scroll {
  color: white;
  font-size: 16px;
  line-height: 1.75;
  white-space: pre-wrap;
  word-wrap: break-word;
  text-align: left;
  font-family: inherit;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 10px;
}

.tac-content-scroll::-webkit-scrollbar {
  width: 8px;
}

.tac-content-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.tac-content-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
}

.tac-content-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

.tac-checkbox-container {
  background-color: #0d111b !important;
  color: white !important;
  padding: 24px !important;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.tac-content >>> p {
  margin-bottom: 16px;
  text-align: left;
  font-size: 16px;
  line-height: 1.75;
}

.tac-content >>> br {
  line-height: 1.75;
}

.tac-checkbox-label {
  font-size: 16px;
  line-height: 1.75;
  color: white;
}

.tac-checkbox-label.disabled-text {
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.scroll-hint {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 8px;
  font-style: italic;
}

.message-header {
  font-size: 18px;
  color: white;
  text-align: center;
}

.v-card__actions {
  background-color: #0d111b !important;
}
</style>

